<!-- src/views/BanOfficerForClientView.vue -->
<template>
  <div class="container">
    <div class="sticky-header-form">
      <h3 class="text-center">BAN OFFICER FOR CLIENT</h3>

      <!-- Create / Edit form -->
      <div class="d-flex align-items-center justify-content-center flex-wrap gap-3 mb-2">
        <div class="d-flex align-items-center gap-2">
          <label class="fw-medium">Officer</label>
          <Multiselect
            v-model="selectedOfficerId"
            :options="officers.map(o => ({ value: o.id, label: o.name }))"
            placeholder="Select or type officer..."
            :searchable="true"
            :clearable="true"
            style="min-width: 220px; --ms-border-color: #dee2e6; --ms-radius: 0.375rem; --ms-px: 0.75rem; --ms-py: 0.25rem;"
          />
        </div>
        <div class="d-flex align-items-center gap-2">
          <label class="fw-medium">Client</label>
          <Multiselect
            v-model="selectedClientId"
            :options="availableClients.map(c => ({ value: c.id, label: c.name }))"
            placeholder="Select client..."
            :searchable="true"
            :clearable="true"
            style="min-width: 220px; --ms-border-color: #dee2e6; --ms-radius: 0.375rem; --ms-px: 0.75rem; --ms-py: 0.25rem;"
          />
        </div>
        <div class="d-flex align-items-center gap-2">
          <label class="fw-medium">Start Date</label>
          <input type="date" v-model="startDate" class="border rounded px-3 py-1">
        </div>
        <div class="d-flex align-items-center gap-2">
          <label class="fw-medium">End Date</label>
          <input type="date" v-model="endDate" class="border rounded px-3 py-1">
        </div>
        <div class="d-flex align-items-center gap-2" v-if="editingId">
          <label class="fw-medium">Banned</label>
          <input type="checkbox" v-model="isBanned" class="form-check-input">
        </div>
      </div>

      <div class="d-flex align-items-center justify-content-center flex-wrap gap-3 mb-4">
        <div class="d-flex align-items-center gap-2 flex-grow-1" style="max-width: 500px;">
          <label class="fw-medium">Reason</label>
          <textarea v-model="reason" rows="1" placeholder="Reason for ban" class="border rounded px-3 py-1 w-100"></textarea>
        </div>
        <button @click="submitForm" class="bg-primary text-white px-6 py-1 rounded hover:bg-blue-700" :disabled="isBusy">
          {{ submitButtonLabel }}
        </button>
        <button v-if="editingId" @click="cancelEdit" class="bg-secondary text-white px-6 py-1 rounded hover:bg-gray-600" :disabled="isBusy">
          Cancel
        </button>
      </div>

      <div class="d-flex align-items-center justify-content-center gap-2 mb-2">
        <label class="fw-medium">Search</label>
        <input type="text" v-model="searchQuery" placeholder="Search by officer, client or reason..." class="border rounded px-3 py-1" style="min-width: 260px;">
      </div>
    </div>

    <!-- Table -->
    <div class="list-scroll-container overflow-x-auto">
      <table class="border-collapse border mx-auto">
        <thead>
          <tr class="bg-warning">
            <th class="border p-2 text-left">Officer</th>
            <th class="border p-2 text-left">Client</th>
            <th class="border p-2 text-left">Reason</th>
            <th class="border p-2 text-left">Start Date</th>
            <th class="border p-2 text-left">End Date</th>
            <th class="border p-2 text-left">Banned</th>
            <th class="border p-2 text-left">Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="!filteredBans || filteredBans.length === 0">
            <td colspan="7" class="border p-2 text-center text-gray-500">{{ searchQuery ? 'No matching records' : 'No records found' }}</td>
          </tr>
          <tr v-for="(ban, index) in filteredBans" :key="index">
            <td class="border p-2 text-left">{{ ban.securityOfficerName || '-' }}</td>
            <td class="border p-2 text-left">{{ ban.clientName || '-' }}</td>
            <td class="border p-2 text-left">{{ ban.reason || '-' }}</td>
            <td class="border p-2 text-left">{{ formatDate(ban.startDate) }}</td>
            <td class="border p-2 text-left">{{ formatDate(ban.endDate) }}</td>
            <td class="border p-2 text-left">{{ ban.isBanned ? 'Yes' : 'No' }}</td>
            <td class="border p-2 text-left">
              <button @click="startEdit(ban)" class="text-blue-600 hover:underline" :disabled="isBusy">Edit</button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import Multiselect from '@vueform/multiselect'
import '@vueform/multiselect/themes/default.css'

const props = defineProps({
  baseUrl: { type: String, required: true }
})

const officers = ref([])
const clients = ref([])
const selectedOfficerId = ref(null)
const selectedClientId = ref(null)
const startDate = ref('')
const endDate = ref('')
const reason = ref('')
const isBanned = ref(true)
const isLoading = ref(false)
const isCreating = ref(false)
const isBusy = ref(false)
const bans = ref([])
const editingId = ref(null)
const searchQuery = ref('')

const submitButtonLabel = computed(() => {
  if (editingId.value) return isCreating.value ? 'Updating...' : 'Update Ban'
  return isCreating.value ? 'Creating...' : 'Create Ban'
})

const availableClients = computed(() => {
  if (editingId.value || !selectedOfficerId.value) return clients.value
  const bannedClientIds = bans.value
    .filter(b => b.securityOfficerId === selectedOfficerId.value && b.isBanned)
    .map(b => b.clientId)
  return clients.value.filter(c => !bannedClientIds.includes(c.id))
})

const filteredBans = computed(() => {
  const query = searchQuery.value.trim().toLowerCase()
  if (!query) return bans.value
  return bans.value.filter(b =>
    (b.securityOfficerName || '').toLowerCase().includes(query) ||
    (b.clientName || '').toLowerCase().includes(query) ||
    (b.reason || '').toLowerCase().includes(query)
  )
})

const formatDate = (value) => value ? new Date(value).toLocaleDateString() : '-'

const toDateInputValue = (value) => value ? new Date(value).toISOString().slice(0, 10) : ''

const loadLookupData = () => {
  const cachedData = localStorage.getItem("lookupData")
  if (cachedData) {
    const parsed = JSON.parse(cachedData)
    officers.value = parsed.officers || []
    clients.value = parsed.companies || []
  }
}

const loadBans = async () => {
  if (!localStorage.token) { return }

  isLoading.value = true
  isBusy.value = true
  try {
    const response = await fetch(props.baseUrl + "SecurityOfficerBans/GetBannedClients", {
      method: "GET",
      headers: { "Authorization": "Bearer " + localStorage.token, "Content-Type": "application/json" }
    })
    if (!response.ok) throw new Error(await response.text())
    const data = await response.json()
    bans.value = data.bans || []
  } catch (e) {
    alert('Failed to load banned clients: ' + e.message)
  } finally {
    isLoading.value = false
    isBusy.value = false
  }
}

const resetForm = () => {
  editingId.value = null
  selectedOfficerId.value = null
  selectedClientId.value = null
  startDate.value = ''
  endDate.value = ''
  reason.value = ''
  isBanned.value = true
}

const startEdit = (ban) => {
  editingId.value = ban.id
  selectedOfficerId.value = ban.securityOfficerId
  selectedClientId.value = ban.clientId
  startDate.value = toDateInputValue(ban.startDate)
  endDate.value = toDateInputValue(ban.endDate)
  reason.value = ban.reason || ''
  isBanned.value = !!ban.isBanned
}

const cancelEdit = () => {
  resetForm()
}

const submitForm = async () => {
  if (!selectedOfficerId.value) { alert('Please select an officer'); return }
  if (!selectedClientId.value) { alert('Please select a client'); return }
  if (!localStorage.token) { alert('Please login first'); return }

  isCreating.value = true
  isBusy.value = true
  try {
    const response = await fetch(props.baseUrl + "SecurityOfficerBans/SaveBanForClient", {
      method: "POST",
      headers: { "Authorization": "Bearer " + localStorage.token, "Content-Type": "application/json" },
      body: JSON.stringify({
        id: editingId.value || 0,
        securityOfficerId: selectedOfficerId.value,
        clientId: selectedClientId.value,
        startDate: startDate.value || null,
        endDate: endDate.value || null,
        reason: reason.value,
        isBanned: editingId.value ? isBanned.value : true
      })
    })
    const data = await response.json()
    if (!response.ok) throw new Error(data.error || data.Error || 'Ban could not be saved.')

    if (editingId.value) {
      const index = bans.value.findIndex(b => b.id === editingId.value)
      if (index !== -1) { bans.value[index] = data.ban }
    } else {
      bans.value.push(data.ban)
    }
    resetForm()
    alert('Ban saved successfully.')
  } catch (e) {
    alert('Failed to save ban: ' + e.message)
  } finally {
    isCreating.value = false
    isBusy.value = false
  }
}

onMounted(() => {
  loadLookupData()
  loadBans()
})
</script>

<style scoped>
.sticky-header-form {
  position: sticky;
  top: 0;
  z-index: 10;
  background: #fff;
  padding-top: 1rem;
  padding-bottom: 0.5rem;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.list-scroll-container {
  max-height: 60vh;
  overflow-y: auto;
}
</style>
